import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, Pressable } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import {
    selectedJobAtom, selectedVersionAtom, jobUserDataAtom, jobCostDetailAtom,
    jobPhaseCodesAtom, dataSourceVersionAtom, phasedForecastAtom, projectCompleteAtom,
    selectedJobDataAtom,
} from '../store/company';
import { loadUserData, loadCostHistoryCombined, fNum, pNum } from '../db/company';
import { standardDisciplineFor } from '../db/wbsStandard';
import moment from 'moment';

const EXCLUDED_CODES = ['1000', '899999', '899998', '990000'];

const METRICS = [
    { key: 'pmEAC', label: 'PM EAC Hours' },
    { key: 'trendingEAC', label: 'Trending EAC Hours' },
    { key: 'budgetHours', label: 'Budget Hours' },
    { key: 'actualHours', label: 'Actual Hours' },
    { key: 'earnedHours', label: 'Earned Hours' },
];

// Same dedup as earnedVsBurned/overview: latest row wins per version/tab/field/row key
const dedupUserRows = (rows) => {
    if (!Array.isArray(rows)) return [];
    const grouped = rows.reduce((acc, item) => {
        const key = `${item.Version_GUID}_${item.BU_ID}_${item.Forecast_Tab}_${item.Forecast_Field_Name}_${item.Forecast_Row_ID}`;
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
    }, {});
    return Object.values(grouped).map(group => group.reduce((latest, current) => {
        const ld = new Date(Math.max(new Date(latest.DSS_Created_Date), new Date(latest.DSS_Updated_Datetime || 0)));
        const cd = new Date(Math.max(new Date(current.DSS_Created_Date), new Date(current.DSS_Updated_Datetime || 0)));
        return cd > ld ? current : latest;
    }));
};

const isLaborCostType = (ct) => ct && ct[0] === '5' && ct[1] === '1' && ct[2] !== '8';

// Replicates the per-phase math from productivityCombined for one forecast version.
// Returns { phases: { code: {...metrics} }, disciplines, disciplineMode, descriptions }
export const computePhaseMetrics = ({ userRows, costRows, versionStart, projectComplete, projectCompleteFallback, regular }) => {
    const dayZero = {};
    const cos = {};
    const scorecard = {};
    const burnToolPhased = {};
    const disciplines = {};
    let disciplineMode = 'user';
    let completeDate = '';

    dedupUserRows(userRows).forEach((d) => {
        if (d.Forecast_Field_Name === 'completionDateForecast') completeDate = d.Forecast_Value;
        if (d?.Forecast_Tab === 'Day Zero Budget') {
            const code = d.Cost_Code;
            if (!code) return;
            if (!dayZero[code]) dayZero[code] = { hours: 0, dollars: 0, uom: '', quantities: 0 };
            if (isLaborCostType(d.Cost_Type)) {
                dayZero[code].hours += pNum(d.Hours) || 0;
                dayZero[code].dollars += pNum(d.Value_Amount) || 0;
            }
            if (d.UoM) dayZero[code].uom = d.UoM;
            dayZero[code].quantities += isNaN(pNum(d.Quantity)) ? 0 : pNum(d.Quantity);
        }
        if (d?.Forecast_Tab === 'Change Order' && d.Status === 'Approved') {
            const code = d.Cost_Code;
            if (!code) return;
            if (!cos[code]) cos[code] = { hours: 0, budgetQty: 0 };
            if (d.Forecast_Field_Name === 'totalLaborHours' ||
                (String(d.Cost_Type || '').trim() === '51100' && d.Forecast_Field_Name !== 'totalEquipmentHours')) cos[code].hours += isNaN(pNum(d.Hours)) ? 0 : pNum(d.Hours);
            cos[code].budgetQty += isNaN(pNum(d.Quantity)) ? 0 : pNum(d.Quantity);
        }
        if (d?.Forecast_Tab === 'Scorecard') {
            if (!scorecard[d.Forecast_Row_ID]) scorecard[d.Forecast_Row_ID] = {};
            scorecard[d.Forecast_Row_ID][d.Forecast_Field_Name] = d.Forecast_Value;
        }
        if (d?.Forecast_Tab === 'Burn Tool Labor Phased') {
            burnToolPhased[d.Forecast_Row_ID] = d;
        }
        if (d?.Forecast_Tab === 'Productivity Tracker') {
            if (d.Forecast_Field_Name === 'discipline') disciplines[d.Forecast_Row_ID] = d.Forecast_Value;
            if (d.Forecast_Field_Name === 'disciplineMode') disciplineMode = d.Forecast_Value === 'standard' ? 'standard' : 'user';
        }
    });

    // ETC hours per phase from the phased burn tool (weekly rows keyed "phase=week")
    const etcHours = {};
    const pc = projectComplete || completeDate || projectCompleteFallback;
    Object.keys(burnToolPhased).forEach((rowId) => {
        if (!rowId.includes('=')) return;
        const [phase, week] = rowId.split('=');
        const row = burnToolPhased[rowId];
        const h = row?.Number_Hours || 0;
        const rate = row?.Rate || 0;
        const units = row?.Number_Units || 0;
        const withinProject = moment(week).isAfter(moment(versionStart).startOf('week')) && moment(week).isBefore(moment(pc));
        if (h && rate && units && withinProject) {
            etcHours[phase] = (etcHours[phase] || 0) + ((!isNaN(pNum(h)) && !isNaN(pNum(units))) ? pNum(h) * pNum(units) : 0);
        }
    });

    // Actual hours + Heavy Job installed quantities + descriptions from cost detail
    const actualHours = {};
    const heavyJobInstalled = {};
    const descriptions = {};
    if (Array.isArray(costRows)) {
        costRows.forEach((row) => {
            if (!row.Phase_Code) return;
            if (row.Phase_Code_Description && !descriptions[row.Phase_Code]) descriptions[row.Phase_Code] = row.Phase_Code_Description;
            if (isLaborCostType(row.Cost_Type_Code)) {
                actualHours[row.Phase_Code] = (actualHours[row.Phase_Code] || 0) + (pNum(row.Total_Hours) || 0);
            }
            if (row.Document_Type === 'FP') {
                heavyJobInstalled[row.Phase_Code] = (heavyJobInstalled[row.Phase_Code] || 0) + (pNum(row.Total_Hours) || 0);
            }
        });
    }

    // Phase universe matches the Productivity tracker: cost detail + day zero + COs
    const allCodes = new Set([
        ...Object.keys(actualHours),
        ...Object.keys(heavyJobInstalled),
        ...Object.keys(descriptions),
        ...Object.keys(dayZero),
        ...Object.keys(cos),
    ]);
    EXCLUDED_CODES.forEach(c => allCodes.delete(c));

    const phases = {};
    allCodes.forEach((code) => {
        const dz = dayZero[code] || { hours: 0, dollars: 0, uom: '', quantities: 0 };
        const co = cos[code] || { hours: 0, budgetQty: 0 };
        const sc = scorecard[code] || {};
        const budgetHours = pNum(dz.hours) + pNum(co.hours);
        const installed = isNaN(pNum(sc.installed)) ? 0 : pNum(sc.installed);
        const totalInstalled = installed + (heavyJobInstalled[code] || 0);
        const plug = isNaN(pNum(sc.plug)) ? 0 : pNum(sc.plug);
        const totalQuantity = pNum(dz.quantities) + pNum(co.budgetQty) + plug;
        const percentComplete = totalQuantity > 0 ? (100 * totalInstalled) / totalQuantity : 0;
        const phaseActualHours = actualHours[code] || 0;
        const budgetHoursQty = totalQuantity ? budgetHours / totalQuantity : 0;
        const actualHoursQty = totalInstalled > 0 ? (percentComplete < 25 ? budgetHoursQty : phaseActualHours / totalInstalled) : 0;
        const forecastedHours = regular === 'regular'
            ? (isNaN(pNum(sc.forecastedHrs)) ? 0 : pNum(sc.forecastedHrs))
            : (etcHours[code] || 0);
        const trendingEAC = percentComplete >= 100 ? phaseActualHours
            : percentComplete > 25 ? actualHoursQty * totalQuantity
                : budgetHours;
        const pmEAC = forecastedHours + phaseActualHours;
        const earnedHours = percentComplete ? percentComplete * budgetHours / 100 : 0;

        phases[code] = {
            code,
            uom: sc.uom || dz.uom || '',
            budgetHours,
            actualHours: phaseActualHours,
            earnedHours,
            trendingEAC,
            pmEAC,
            installedQty: totalInstalled,
            totalQuantity,
            percentComplete,
        };
    });

    return { phases, disciplines, disciplineMode, descriptions };
};

const versionLabel = (v) => v
    ? moment(v.Version_Start_DateTime).format('MMM D, YYYY') + (v.Version_Type?.includes('WIP') ? ' (WIP)' : '')
    : '';

const s = {
    table: { borderCollapse: 'separate', borderSpacing: 0, fontSize: 11, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', minWidth: 760, alignSelf: 'flex-start' },
    th: { padding: '6px 10px', fontSize: 9, fontWeight: 600, color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'right', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em' },
    thLeft: { padding: '6px 10px', fontSize: 9, fontWeight: 600, color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em' },
    td: { padding: '5px 10px', fontSize: 11, color: '#1e293b', borderBottom: '1px solid #f1f5f9', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
    tdLeft: { padding: '5px 10px', fontSize: 11, color: '#1e293b', borderBottom: '1px solid #f1f5f9', textAlign: 'left', whiteSpace: 'nowrap' },
    tdSection: { padding: '6px 10px', fontSize: 11, fontWeight: 700, color: '#0f172a', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
    tdSectionLeft: { padding: '6px 10px', fontSize: 11, fontWeight: 700, color: '#0f172a', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', textAlign: 'left', whiteSpace: 'nowrap' },
    tfoot: { padding: '6px 10px', fontSize: 11, fontWeight: 700, color: '#0f172a', borderTop: '2px solid #e2e8f0', background: '#f8fafc', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
    select: { height: 28, fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 4, padding: '2px 6px', background: 'white', color: '#334155' },
};

const deltaColor = (delta) => delta > 0 ? '#dc2626' : delta < 0 ? '#16a34a' : '#94a3b8';

const WipVariance = () => {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [selectedJobData] = useAtom(selectedJobDataAtom);
    const [jobUserData] = useAtom(jobUserDataAtom);
    const [jobCostDetail] = useAtom(jobCostDetailAtom);
    const [jobPhaseCodes] = useAtom(jobPhaseCodesAtom);
    const [versions] = useAtom(dataSourceVersionAtom);
    const [regular] = useAtom(phasedForecastAtom);
    const [projectComplete] = useAtom(projectCompleteAtom);

    const [metricKey, setMetricKey] = useState('pmEAC');
    const [compareGuid, setCompareGuid] = useState(null);
    const [compareUserData, setCompareUserData] = useState(null);
    const [compareCostDetail, setCompareCostDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    const currentVersionInfo = useMemo(
        () => (versions || []).find(v => v.Version_GUID === selectedVersion),
        [versions, selectedVersion]);

    // Versions available for comparison: everything except the current one, newest first
    const compareOptions = useMemo(() => {
        if (!versions?.length) return [];
        const allowed = ['WIP', 'Regular', 'Reg-locked', 'Unassigned', 'WIP-locked'];
        return versions
            .filter(v => allowed.includes(v.Version_Type) && v.Version_GUID !== selectedVersion)
            .sort((a, b) => new Date(b.Version_Start_DateTime) - new Date(a.Version_Start_DateTime));
    }, [versions, selectedVersion]);

    // Default comparison: the most recent version before the current one
    useEffect(() => {
        if (compareGuid && compareOptions.some(v => v.Version_GUID === compareGuid)) return;
        if (!compareOptions.length) { setCompareGuid(null); return; }
        const currentDate = currentVersionInfo ? moment(currentVersionInfo.Version_Start_DateTime) : null;
        const prior = currentDate
            ? compareOptions.find(v => moment(v.Version_Start_DateTime).isBefore(currentDate))
            : null;
        setCompareGuid((prior || compareOptions[0]).Version_GUID);
    }, [compareOptions, currentVersionInfo]);

    const compareVersionInfo = useMemo(
        () => (versions || []).find(v => v.Version_GUID === compareGuid),
        [versions, compareGuid]);

    // Load the comparison version's forecast + cost detail
    useEffect(() => {
        if (!selectedJob || !compareGuid) return;
        let cancelled = false;
        setLoading(true);
        setCompareUserData(null);
        setCompareCostDetail(null);
        Promise.all([
            loadUserData({ id: selectedJob, forecastGuid: compareGuid }),
            loadCostHistoryCombined({ id: selectedJob, versionGuid: compareGuid }),
        ]).then(([ud, cd]) => {
            if (cancelled) return;
            setCompareUserData(ud?.data || []);
            setCompareCostDetail(cd?.data || []);
            setLoading(false);
        }).catch(() => {
            if (cancelled) return;
            setCompareUserData([]);
            setCompareCostDetail([]);
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, [selectedJob, compareGuid]);

    const current = useMemo(() => {
        if (!jobUserData?.length && !jobCostDetail?.length) return null;
        return computePhaseMetrics({
            userRows: jobUserData,
            costRows: jobCostDetail,
            versionStart: currentVersionInfo?.Version_Start_DateTime,
            projectComplete,
            regular,
        });
    }, [jobUserData, jobCostDetail, currentVersionInfo, projectComplete, regular]);

    const compare = useMemo(() => {
        if (!compareUserData && !compareCostDetail) return null;
        return computePhaseMetrics({
            userRows: compareUserData || [],
            costRows: compareCostDetail || [],
            versionStart: compareVersionInfo?.Version_Start_DateTime,
            // Prefer the comparison version's own completion-date forecast; fall back to the atom
            projectComplete: '',
            projectCompleteFallback: projectComplete,
            regular,
        });
    }, [compareUserData, compareCostDetail, compareVersionInfo, projectComplete, regular]);

    const metric = METRICS.find(m => m.key === metricKey) || METRICS[0];

    // Grouped rows: discipline sections with per-phase variance, ordered like Productivity
    const grouped = useMemo(() => {
        if (!current || !compare) return null;
        const codes = new Set([...Object.keys(current.phases), ...Object.keys(compare.phases)]);
        const disciplineMode = current.disciplineMode;
        const standard = disciplineMode === 'standard';
        const areasPresent = new Set();
        if (standard) {
            codes.forEach((code) => {
                const std = standardDisciplineFor(code);
                if (std) areasPresent.add(std.area);
            });
        }
        const multiArea = areasPresent.size > 1;

        const rows = [];
        codes.forEach((code) => {
            const cur = current.phases[code];
            const prev = compare.phases[code];
            const curVal = cur ? pNum(cur[metric.key]) : 0;
            const prevVal = prev ? pNum(prev[metric.key]) : 0;
            let discipline;
            let order = 999999;
            if (standard) {
                const std = standardDisciplineFor(code);
                discipline = std ? (multiArea ? 'Area ' + std.area + ' - ' + std.discipline : std.discipline) : 'Other';
                order = std ? std.area * 10000 + std.order : 999999;
            } else {
                discipline = current.disciplines[code] || compare.disciplines[code] || 'Other';
            }
            rows.push({
                code,
                description: current.descriptions[code] || compare.descriptions[code] || jobPhaseCodes?.[code] || '',
                discipline,
                order,
                curVal,
                prevVal,
                delta: curVal - prevVal,
            });
        });

        const sections = {};
        rows.forEach((r) => {
            if (!sections[r.discipline]) sections[r.discipline] = { discipline: r.discipline, order: r.order, rows: [], curVal: 0, prevVal: 0, delta: 0 };
            const sec = sections[r.discipline];
            sec.rows.push(r);
            if (r.order < sec.order) sec.order = r.order;
            sec.curVal += r.curVal;
            sec.prevVal += r.prevVal;
            sec.delta += r.delta;
        });
        const list = Object.values(sections);
        list.forEach(sec => sec.rows.sort((a, b) => a.code.localeCompare(b.code)));
        list.sort((a, b) => standard
            ? (a.order - b.order) || a.discipline.localeCompare(b.discipline)
            : a.rows[0].code.localeCompare(b.rows[0].code));

        const total = {
            curVal: list.reduce((t, sec) => t + sec.curVal, 0),
            prevVal: list.reduce((t, sec) => t + sec.prevVal, 0),
        };
        total.delta = total.curVal - total.prevVal;
        return { sections: list, total };
    }, [current, compare, metric.key, jobPhaseCodes]);

    const curLabel = versionLabel(currentVersionInfo) || 'Current';
    const prevLabel = versionLabel(compareVersionInfo) || 'Prior';
    const pctChange = (delta, prevVal) => prevVal !== 0 ? (delta / Math.abs(prevVal)) * 100 : null;

    const exportExcel = async () => {
        if (!grouped) return;
        setExporting(true);
        try {
            const ExcelJS = (await import('exceljs')).default;
            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet('Variance');
            sheet.addRow([`${metric.label} Variance — ${curLabel} vs ${prevLabel}`]);
            sheet.getRow(1).font = { bold: true, size: 12 };
            sheet.addRow([]);
            const header = sheet.addRow(['Discipline', 'Phase Code', 'Description', curLabel, prevLabel, 'Delta Hrs', '% Change']);
            header.font = { bold: true };
            grouped.sections.forEach((sec) => {
                const secRow = sheet.addRow([sec.discipline, '', '', sec.curVal, sec.prevVal, sec.delta,
                    sec.prevVal !== 0 ? sec.delta / Math.abs(sec.prevVal) : '']);
                secRow.font = { bold: true };
                sec.rows.forEach((r) => {
                    sheet.addRow([sec.discipline, r.code, r.description, r.curVal, r.prevVal, r.delta,
                        r.prevVal !== 0 ? r.delta / Math.abs(r.prevVal) : '']);
                });
            });
            const totalRow = sheet.addRow(['TOTAL', '', '', grouped.total.curVal, grouped.total.prevVal, grouped.total.delta,
                grouped.total.prevVal !== 0 ? grouped.total.delta / Math.abs(grouped.total.prevVal) : '']);
            totalRow.font = { bold: true };
            sheet.columns = [{ width: 26 }, { width: 12 }, { width: 30 }, { width: 16 }, { width: 16 }, { width: 12 }, { width: 12 }];
            [4, 5, 6].forEach(i => sheet.getColumn(i).numFmt = '#,##0');
            sheet.getColumn(7).numFmt = '0.0%';

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const ts = currentVersionInfo?.Version_Start_DateTime ? moment(currentVersionInfo.Version_Start_DateTime).format('MM.DD') : '';
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = [selectedJob, ts, 'Hour WIP Variance.xlsx'].filter(Boolean).join(' - ');
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('WIP Variance export failed', e);
        }
        setExporting(false);
    };

    if (!selectedJob) {
        return (
            <View style={{ padding: 40, alignItems: 'center' }}>
                <Text style={{ fontSize: 14, color: '#6b7280' }}>Select a job to view the hour WIP variance report.</Text>
            </View>
        );
    }

    return (
        <View style={{ padding: 20 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                <Text style={{ fontSize: 14, fontWeight: '700', color: '#0f172a' }}>Hour WIP Variance</Text>
                <select value={metricKey} onChange={e => setMetricKey(e.target.value)} style={s.select}>
                    {METRICS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
                </select>
                <Text style={{ fontSize: 12, color: '#475569' }}>{curLabel}</Text>
                <Text style={{ fontSize: 12, color: '#94a3b8' }}>vs</Text>
                <select value={compareGuid || ''} onChange={e => setCompareGuid(pNum(e.target.value) || e.target.value)} style={s.select}>
                    {compareOptions.map(v => (
                        <option key={v.Version_GUID} value={v.Version_GUID}>{versionLabel(v)}</option>
                    ))}
                </select>
                <Pressable
                    onPress={exportExcel}
                    disabled={exporting || !grouped}
                    style={{ cursor: 'pointer', backgroundColor: '#eff6ff', padding: '4px 10px', borderRadius: 4, border: '1px solid #bfdbfe' }}
                >
                    <Text style={{ fontSize: 12, color: '#2563eb', fontWeight: '600' }}>{exporting ? 'Exporting…' : 'Export Excel'}</Text>
                </Pressable>
            </View>

            {loading || !grouped ? (
                <View style={{ padding: 40, alignItems: 'center' }}>
                    <Text style={{ fontSize: 13, color: '#6b7280' }}>{loading ? 'Loading comparison version…' : 'No data available for this job.'}</Text>
                </View>
            ) : (
                <table style={s.table}>
                    <thead>
                        <tr>
                            <th style={s.thLeft}>Phase Code</th>
                            <th style={s.thLeft}>Description</th>
                            <th style={s.th}>{metric.label} — {curLabel}</th>
                            <th style={s.th}>{metric.label} — {prevLabel}</th>
                            <th style={s.th}>Delta Hrs</th>
                            <th style={s.th}>% Change</th>
                        </tr>
                    </thead>
                    <tbody>
                        {grouped.sections.map((sec) => {
                            const secPct = pctChange(sec.delta, sec.prevVal);
                            return (
                                <React.Fragment key={sec.discipline}>
                                    <tr>
                                        <td style={s.tdSectionLeft} colSpan={2}>{sec.discipline}</td>
                                        <td style={s.tdSection}>{fNum(sec.curVal, 1)}</td>
                                        <td style={s.tdSection}>{fNum(sec.prevVal, 1)}</td>
                                        <td style={{ ...s.tdSection, color: deltaColor(sec.delta) }}>{sec.delta > 0 ? '+' : ''}{fNum(sec.delta, 1)}</td>
                                        <td style={{ ...s.tdSection, color: deltaColor(sec.delta) }}>{secPct == null ? '—' : (secPct > 0 ? '+' : '') + fNum(secPct, 1) + '%'}</td>
                                    </tr>
                                    {sec.rows.map((r) => {
                                        const pct = pctChange(r.delta, r.prevVal);
                                        const dim = r.curVal === 0 && r.prevVal === 0;
                                        return (
                                            <tr key={r.code} style={{ transition: 'background .15s' }}
                                                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                                onMouseLeave={e => e.currentTarget.style.background = ''}>
                                                <td style={{ ...s.tdLeft, color: dim ? '#94a3b8' : '#1e293b' }}>{r.code}</td>
                                                <td style={{ ...s.tdLeft, color: dim ? '#94a3b8' : '#1e293b' }}>{r.description}</td>
                                                <td style={{ ...s.td, color: dim ? '#94a3b8' : '#1e293b' }}>{fNum(r.curVal, 1)}</td>
                                                <td style={{ ...s.td, color: dim ? '#94a3b8' : '#1e293b' }}>{fNum(r.prevVal, 1)}</td>
                                                <td style={{ ...s.td, color: dim ? '#cbd5e1' : deltaColor(r.delta) }}>{r.delta > 0 ? '+' : ''}{fNum(r.delta, 1)}</td>
                                                <td style={{ ...s.td, color: dim ? '#cbd5e1' : deltaColor(r.delta) }}>{pct == null ? '—' : (pct > 0 ? '+' : '') + fNum(pct, 1) + '%'}</td>
                                            </tr>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td style={{ ...s.tfoot, textAlign: 'left' }} colSpan={2}>TOTAL</td>
                            <td style={s.tfoot}>{fNum(grouped.total.curVal, 1)}</td>
                            <td style={s.tfoot}>{fNum(grouped.total.prevVal, 1)}</td>
                            <td style={{ ...s.tfoot, color: deltaColor(grouped.total.delta) }}>{grouped.total.delta > 0 ? '+' : ''}{fNum(grouped.total.delta, 1)}</td>
                            <td style={{ ...s.tfoot, color: deltaColor(grouped.total.delta) }}>
                                {grouped.total.prevVal !== 0 ? (grouped.total.delta > 0 ? '+' : '') + fNum(pctChange(grouped.total.delta, grouped.total.prevVal), 1) + '%' : '—'}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            )}
        </View>
    );
};

export default WipVariance;
